/**
 * Contact-form validation and submission.
 *
 * Client-side validation exists for the person filling the form in — it drives
 * the accessible inline errors. It is not a security boundary; the Lambda in
 * server/contact revalidates everything and is the only thing that decides
 * whether an email gets sent.
 */

export type ContactField =
  | "firstName"
  | "lastName"
  | "email"
  | "childAge"
  | "message";

export type FieldErrors = Partial<Record<ContactField | "captcha", string>>;

export interface ContactValues {
  firstName: string;
  lastName: string;
  email: string;
  childAge: string;
  message: string;
}

export const EMPTY_CONTACT: ContactValues = {
  firstName: "",
  lastName: "",
  email: "",
  childAge: "",
  message: "",
};

/** Mirrors the caps enforced in the Lambda. */
export const MAX_LENGTHS: Record<ContactField, number> = {
  firstName: 100,
  lastName: 100,
  email: 254,
  childAge: 60,
  message: 4000,
};

export const FIELD_LABELS: Record<ContactField, string> = {
  firstName: "First name",
  lastName: "Last name",
  email: "Email address",
  childAge: "Child's age",
  message: "How can we help?",
};

const EMAIL_RE = /^[^\s@,;:<>()[\]\\"]+@[^\s@,;:<>()[\]\\".]+\.[a-zA-Z]{2,}$/;

/**
 * WCAG 3.3.1 / 3.3.3 — each message names the field and says how to fix it,
 * rather than just flagging that something is wrong.
 */
export function validateContact(values: ContactValues): FieldErrors {
  const errors: FieldErrors = {};

  if (!values.firstName.trim()) {
    errors.firstName = "Enter your first name.";
  } else if (values.firstName.trim().length > MAX_LENGTHS.firstName) {
    errors.firstName = `Use ${MAX_LENGTHS.firstName} characters or fewer.`;
  }

  if (!values.lastName.trim()) {
    errors.lastName = "Enter your last name.";
  } else if (values.lastName.trim().length > MAX_LENGTHS.lastName) {
    errors.lastName = `Use ${MAX_LENGTHS.lastName} characters or fewer.`;
  }

  if (!values.email.trim()) {
    errors.email = "Enter your email address so we can reply.";
  } else if (!EMAIL_RE.test(values.email.trim())) {
    errors.email = "Enter an email address in the format name@example.com.";
  }

  if (values.childAge.trim().length > MAX_LENGTHS.childAge) {
    errors.childAge = `Use ${MAX_LENGTHS.childAge} characters or fewer.`;
  }

  if (!values.message.trim()) {
    errors.message = "Tell us a little about what you would like help with.";
  } else if (values.message.trim().length > MAX_LENGTHS.message) {
    errors.message = `Use ${MAX_LENGTHS.message} characters or fewer.`;
  }

  return errors;
}

export const CONTACT_ENDPOINT = import.meta.env.VITE_CONTACT_ENDPOINT ?? "";
export const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY ?? "";

export type SubmitResult =
  | { ok: true }
  | { ok: false; message: string; fieldErrors?: FieldErrors };

/** Server error codes mapped to something a parent would understand. */
const ERROR_MESSAGES: Record<string, string> = {
  captcha_missing: "Please complete the spam protection check and try again.",
  captcha_failed: "The spam protection check did not pass. Please try again.",
  captcha_unavailable:
    "We could not reach the spam protection service. Please try again in a moment.",
  too_fast: "That was submitted unusually quickly. Please try again.",
  validation_failed: "Please check the highlighted fields and try again.",
  payload_too_large: "That message is too long. Please shorten it and try again.",
  forbidden_origin: "This form could not be submitted from here.",
  send_failed:
    "We could not send your message just now. Please try again, or email us directly.",
  server_misconfigured:
    "The form is temporarily unavailable. Please email us directly.",
};

const GENERIC_ERROR =
  "We could not send your message. Please try again, or reach out by phone or social media.";

export async function submitContact(input: {
  values: ContactValues;
  emailMarketingConsent: boolean;
  turnstileToken: string | null;
  /** ms since the form mounted — the Lambda rejects implausibly fast posts. */
  elapsedMs: number;
}): Promise<SubmitResult> {
  if (!CONTACT_ENDPOINT) {
    return {
      ok: false,
      message:
        "The form is not connected yet. Please email us directly for now.",
    };
  }

  if (!input.turnstileToken) {
    return {
      ok: false,
      message: ERROR_MESSAGES.captcha_missing,
      fieldErrors: { captcha: ERROR_MESSAGES.captcha_missing },
    };
  }

  let res: Response;
  try {
    res = await fetch(CONTACT_ENDPOINT, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        firstName: input.values.firstName.trim(),
        lastName: input.values.lastName.trim(),
        email: input.values.email.trim(),
        childAge: input.values.childAge.trim(),
        message: input.values.message.trim(),
        emailMarketingConsent: input.emailMarketingConsent,
        turnstileToken: input.turnstileToken,
        elapsedMs: input.elapsedMs,
        // Honeypot. Always empty for a real person — the field is hidden from
        // both sighted users and assistive tech, so only a bot fills it.
        company: "",
      }),
      signal: AbortSignal.timeout(15000),
    });
  } catch {
    return { ok: false, message: GENERIC_ERROR };
  }

  if (res.status === 429) {
    return {
      ok: false,
      message: "Too many attempts just now. Please wait a moment and try again.",
    };
  }

  let body: { ok?: boolean; error?: string; fieldErrors?: Record<string, string> } = {};
  try {
    body = await res.json();
  } catch {
    // Fall through to the status check below.
  }

  if (res.ok && body.ok) return { ok: true };

  const code = body.error ?? "";
  const message = ERROR_MESSAGES[code] ?? GENERIC_ERROR;

  // The server returns terse codes per field; surface our own wording.
  let fieldErrors: FieldErrors | undefined;
  if (body.fieldErrors) {
    fieldErrors = {};
    for (const [field, reason] of Object.entries(body.fieldErrors)) {
      const label = FIELD_LABELS[field as ContactField] ?? field;
      fieldErrors[field as ContactField] =
        reason === "required"
          ? `${label} is required.`
          : reason === "invalid"
            ? `Enter a valid ${label.toLowerCase()}.`
            : `${label} is too long.`;
    }
  }

  return { ok: false, message, fieldErrors };
}
